import { FEATURE_ACCESS, FEATURES } from "../constants/permissions.constants";
import LocalStorage from "./localStorage";

/**
 * @param {object|null|undefined} user
 * @param {string} permission
 */
export function hasPermission(user, permission) {
  if (!user || !permission) return false;
  const list = user.permissions;
  return Array.isArray(list) && list.includes(permission);
}

/** @param {object|null|undefined} user @param {readonly string[]} permissions */
export function hasAnyPermission(user, permissions) {
  return permissions.some((p) => hasPermission(user, p));
}

/**
 * Check access for a FEATURES.* key using FEATURE_ACCESS rules.
 * @param {object|null|undefined} user
 * @param {string} feature
 */
export function userCanAccess(user, feature) {
  const rule = FEATURE_ACCESS[feature];
  if (!rule) return false;

  if (rule.allOf?.length) {
    return rule.allOf.every((p) => hasPermission(user, p));
  }

  if (rule.anyOf?.length) {
    return hasAnyPermission(user, rule.anyOf);
  }

  return false;
}

/** @param {object|null|undefined} user */
export function getUserCapabilities(user) {
  return {
    reporting: userCanAccess(user, FEATURES.REPORTING),
    userManagement: userCanAccess(user, FEATURES.USER_MANAGEMENT),
    userManagementChange: userCanAccess(
      user,
      FEATURES.USER_MANAGEMENT_CHANGE
    ),
    userManagementAdd: userCanAccess(user, FEATURES.USER_MANAGEMENT_ADD),
    userManagementDelete: userCanAccess(user, FEATURES.USER_MANAGEMENT_DELETE),
    userManagementMedication: userCanAccess(
      user,
      FEATURES.USER_MANAGEMENT_MEDICATION
    ),
    hospitalsView: userCanAccess(user, FEATURES.HOSPITALS_VIEW),
    auditTrail: userCanAccess(user, FEATURES.AUDIT_TRAIL),
    pafReview: userCanAccess(user, FEATURES.PAF_REVIEW),
    reviewOutcomeOptions: userCanAccess(user, FEATURES.REVIEW_OUTCOME_OPTIONS),
    pafEdit: userCanAccess(user, FEATURES.PAF_EDIT),
  };
}

export function canViewReporting(user) {
  return userCanAccess(user, FEATURES.REPORTING);
}

/** View User Management nav and page (users.view_user). */
export function canManageUsers(user) {
  return userCanAccess(user, FEATURES.USER_MANAGEMENT);
}

export function canChangeUser(user) {
  return userCanAccess(user, FEATURES.USER_MANAGEMENT_CHANGE);
}

/** Activate/deactivate registered products (users.view_usermedication). */
export function canManageUserMedication(user) {
  return userCanAccess(user, FEATURES.USER_MANAGEMENT_MEDICATION);
}

export function canAddUser(user) {
  return userCanAccess(user, FEATURES.USER_MANAGEMENT_ADD);
}

export function canDeleteUser(user) {
  return userCanAccess(user, FEATURES.USER_MANAGEMENT_DELETE);
}

export function canViewHospitals(user) {
  return userCanAccess(user, FEATURES.HOSPITALS_VIEW);
}

/** View the Audit Trail nav item and page (users.view_audit_trail). */
export function canViewAuditTrail(user) {
  return userCanAccess(user, FEATURES.AUDIT_TRAIL);
}

/** Submit a PAF review (users.review_paf). */
export function canReviewPaf(user) {
  return userCanAccess(user, FEATURES.PAF_REVIEW);
}

/** Load review outcome options (settings.view_reviewoutcomeoption). */
export function canViewReviewOutcomeOptions(user) {
  return userCanAccess(user, FEATURES.REVIEW_OUTCOME_OPTIONS);
}

/** Edit a prescription / PAF (prescriptions.change_prescription). */
export function canChangePrescription(user) {
  return userCanAccess(user, FEATURES.PAF_EDIT);
}

/**
 * Merge cached session with fresh profile payload from the API.
 */
export function mergeUserSession(cachedUser, profileData) {
  if (!profileData || typeof profileData !== "object") {
    return cachedUser;
  }

  return {
    ...cachedUser,
    ...profileData,
    isLoggedIn: true,
    permissions: Array.isArray(profileData.permissions)
      ? [...profileData.permissions]
      : [],
  };
}

/** Persist merged session (permissions refreshed from server). */
export function persistUserSession(user) {
  if (!user) return;
  localStorage.setItem(LocalStorage.USER_NHSPORTAL, JSON.stringify(user));
}
